import * as v from 'valibot';
import {schemaLogger} from './schema-logger';

/**
 * @param schemaInput Valibot schema object OR a named object like { MySchema } to automatically capture the name
 * @param callback (error, data<typeof schema>)
 *  **callback will still return the original data update even if the schema validation
 *  does not pass. This is to avoid break changes in the app that may have been missed
 * while this schema is being created and fleshed out
 * @param source meta data for debugging
 * @param schemaName optional name for the schema (ignored if schemaInput is a named object)
 * @returns void | undefined
 */
export function validateSubscriptionSchema<
  TSchema extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>,
>(
  schemaInput: TSchema | Record<string, TSchema>,
  callback: (error: unknown, data: v.InferOutput<TSchema> | undefined) => void,
  source = 'unknown source',
  schemaName?: string,
) {
  let schema: TSchema;
  let finalSchemaName = schemaName;

  if (
    !schemaName
    && typeof schemaInput === 'object'
    && schemaInput !== null
    && !('type' in schemaInput)
    && !('kind' in schemaInput)
    && !('async' in schemaInput)
  ) {
    const keys = Object.keys(schemaInput);
    const [namedSchemaEntry] = Object.entries(schemaInput);
    if (keys.length === 1 && namedSchemaEntry !== undefined) {
      const [namedSchemaKey, namedSchema] = namedSchemaEntry;
      finalSchemaName = namedSchemaKey;
      schema = namedSchema;
    } else {
      schema = schemaInput as unknown as TSchema;
    }
  } else {
    schema = schemaInput as TSchema;
  }

  return (error: unknown, data: unknown) => {
    if (error) {
      callback(error, data);
      return;
    }

    const result = v.safeParse(schema, data);
    if (!result.success) {
      schemaLogger.log(source, schema, result, data, finalSchemaName);

      callback(null, data);
      return;
    }

    callback(null, data);
  };
}

/**
 * Safe parse helper that avoids exposing valibot types to callers.
 */
export function safeParseSchema(schema: unknown, data: unknown) {
  return v.safeParse(schema as v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>, data);
}

/**
 * @param value Date-like string in various parsable formats, or an actual Date object
 * @returns true if literal Date, or string is a valid parsable date.
 */
export const dateString = v.custom<string | Date>(value => {
  // Literal Date object
  if (typeof value !== 'string') {
    if (value instanceof Date) {
      return true;
    }

    return false;
  }

  const date = new Date(value);
  // YYYY-MM-DD
  if (!isNaN(date.getTime())) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (match) {
      const [_, year, month, day] = match;
      if (
        date.getUTCFullYear() !== Number(year)
        || date.getUTCMonth() + 1 !== Number(month)
        || date.getUTCDate() !== Number(day)
      ) {
        return false;
      }
    }

    return true;
  }

  // YYYYMMDD
  if (/^\d{8}$/.test(value)) {
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(4, 6));
    const day = Number(value.slice(6, 8));
    const dateObj = new Date(Date.UTC(year, month - 1, day));
    return (
      dateObj.getUTCFullYear() === year
      && dateObj.getUTCMonth() + 1 === month
      && dateObj.getUTCDate() === day
    );
  }

  return false;
}, 'Invalid date string');

/**
 * @param value comma separated string
 * @returns true if value is string and optionally contains commas
 */
export const commaSeparatedStringArray = v.custom<string>(
  value => typeof value === 'string' && /^(\s*\w+\s*)(,\s*\w+\s*)*$|^\s*\w+\s*$/.test(value),
  'Must be a comma-separated list or a single string',
);

/**
 * @description confirms that the value is a number or number-parsable string and returns true if greater than provided min value
 * @param minVal The minimum value
 * @returns boolean
 */
export const minimumValue = (minVal = 0) =>
  v.pipe(
    v.custom<string | number>(
      value => typeof Number(value) === 'number' && !isNaN(Number(value)),
      'Must be Number or number-parsable string',
    ),
    v.transform(Number),
    v.minValue(minVal),
  );

/**
 * @description confirms that the value is a number or number-parsable string and returns true if less than provided max value
 * @param minVal The maximum value
 * @returns boolean
 */
export const maximumValue = (maxVal: number) =>
  v.pipe(
    v.custom(
      value => typeof Number(value) === 'number' && !isNaN(Number(value)),
      'Must be Number or number-parsable string',
    ),
    v.transform(Number),
    v.maxValue(maxVal),
  );

/**
 * @description Validates a JSON string against a valibot schema.
 * @param schema valibot schema object
 * @param jsonString JSON string to validate
 * @returns true if the parsed JSON validates successfully, false otherwise
 */
export const validateJsonString = (
  schema: v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>,
  jsonString: string,
): boolean => {
  try {
    const parsed = JSON.parse(jsonString);
    const result = v.safeParse(schema, parsed);
    return result.success;
  } catch {
    return false;
  }
};

/* v8 ignore start */
/**
 * Debugging helper method
 */
export function parseValibotIssues<
  TSchema extends v.BaseSchema<unknown, unknown, v.BaseIssue<unknown>>,
>(result: v.SafeParseResult<TSchema>): string {
  if (result.success || !result.issues) {return '';}

  const issuesList = result.issues.map(issue => {
    const path = issue.path?.map((pathItem: {key: unknown}) => pathItem.key).filter(Boolean).join('.') ?? '';
    return `[${path}]: received '${issue.received}', expected ${issue.expected}`;
  }).join('\n');

  return `\n{\n${issuesList}\n}`;
}
/* v8 ignore stop */
